import { CardContent, Box, Button } from '@mui/material';
import styled from 'styled-components';
import { Typography, Spacer } from './styles';
import { MinimalCard } from 'components/styled';
import { convertISODateToRegularDateString } from 'utils';
import { GenericTeamInterface } from 'types';
import { conditionallyRenderElement } from 'utils';
import { useCsmAssignments } from 'CsmModule/hooks/useCsmAssignments';
import { getAiSuggestionsState } from 'TeamModule/utils';
import EllipsisText from 'components/EllipsisText';
import Copy from 'components/Copy';

const defaultValue = 'N/A';

type AboutSectionPropsType = {
  selectedTeam?: GenericTeamInterface;
  tenant?: string;
  realm?: string;
  realmType?: string;
  teamId?: number;
  /** UUID from search API */
  uuid?: string;
  /** Boolean flags from search API */
  ssoEnabled?: boolean;
  openApiEnabled?: boolean;
  ddiEnabled?: boolean;
  /** Opens the Enable DDI confirmation flow; the button renders only when DDI is known-disabled (GLX-1567) */
  onEnableDdi?: () => void;
  /** Disables the Enable button while the provisioning request is in flight to prevent duplicate POSTs */
  isEnablingDdi?: boolean;
  internalTestTeam?: boolean;
  chelloCompliant?: boolean;
  aiEnabled?: boolean;
  /** A blocked team's AI suggestions are off and cannot be turned on; shown over aiEnabled. */
  aiBlocked?: boolean;
  /** Nicknames from search API — shown as "Also known as" */
  nicknames?: string[] | null;
  /** Date fields from search API */
  lastBackfilled?: string;
  scheduledArchiveDate?: string | null;
  archivedAt?: string | null;
  reasonToArchive?: string | null;
  /** CSM name from search API - used as fallback */
  searchApiCsm?: string | null;
  /**
   * Active internal-contractor count. Sourced from the overall count statistics
   * endpoint (the only per-team API exposing it) rather than the rails teams/all
   * payload, which has no internal-contractor count (GLX-1842).
   */
  internalContractorsCount?: number;
  /** Loading state for the statistics query that supplies internalContractorsCount */
  isLoadingInternalContractors?: boolean;
  /** Loading state for the slow rails API (counts, salesforce, etc.) */
  isLoadingDetailedData?: boolean;
  /** Loading state for the fast search API (tenant, realm, etc.) */
  isLoadingBasicData?: boolean;
  /** @deprecated Use isLoadingDetailedData instead */
  isLoadingTeamData?: boolean;
};

const AboutSectionStyler = styled(Box)`
  .ellipsis-text {
    display: block;
  }
`;

/** `undefined` means the flag never arrived from the API, which renders as N/A rather than "No". */
const renderBooleanFlag = (value?: boolean) =>
  value === undefined ? defaultValue : value ? 'Yes' : 'No';

const formatNicknames = (nicknames?: string[] | null) =>
  nicknames
    ?.map((nickname) => nickname.trim())
    .filter((nickname) => nickname.length > 0)
    .join(', ') || defaultValue;

type BasicTeamInfoPropsType = Pick<
  AboutSectionPropsType,
  | 'uuid'
  | 'realmType'
  | 'nicknames'
  | 'internalTestTeam'
  | 'ddiEnabled'
  | 'onEnableDdi'
  | 'isEnablingDdi'
  | 'ssoEnabled'
  | 'openApiEnabled'
  | 'chelloCompliant'
  | 'aiEnabled'
  | 'aiBlocked'
  | 'lastBackfilled'
  | 'scheduledArchiveDate'
  | 'archivedAt'
  | 'reasonToArchive'
> & {
  tenant: string;
  realm: string;
  teamId?: number;
};

/** Fields backed by the fast search API. */
const BasicTeamInfo = (props: BasicTeamInfoPropsType) => (
  <>
    {props.uuid && (
      <Typography
        variant="body2"
        gutterBottom
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          wordBreak: 'break-all',
        }}
      >
        UUID: {props.uuid}
        <Copy textToCopy={props.uuid} size="small" />
      </Typography>
    )}
    <Typography variant="body2" gutterBottom>
      Tenant: {props.tenant}
    </Typography>
    <Typography variant="body2" gutterBottom>
      Isolated Database Name: {props.realm}
    </Typography>
    <Typography variant="body2" gutterBottom>
      Realm Type: {props.realmType || defaultValue}
    </Typography>
    <Typography variant="body2" gutterBottom>
      Team ID: {props.teamId || defaultValue}
    </Typography>
    <Typography variant="body2" gutterBottom>
      Also known as: {formatNicknames(props.nicknames)}
    </Typography>
    <Typography variant="body2" gutterBottom>
      Is Test Team: {renderBooleanFlag(props.internalTestTeam)}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      gutterBottom
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
    >
      DDI Enabled: {renderBooleanFlag(props.ddiEnabled)}
      {props.ddiEnabled === false && props.onEnableDdi && (
        <Button
          size="small"
          variant="outlined"
          onClick={props.onEnableDdi}
          disabled={props.isEnablingDdi}
          sx={{ ml: 1, py: 0 }}
        >
          {props.isEnablingDdi ? 'Enabling…' : 'Enable'}
        </Button>
      )}
    </Typography>
    <Typography variant="body2" gutterBottom>
      SSO Enabled: {renderBooleanFlag(props.ssoEnabled)}
    </Typography>
    <Typography variant="body2" gutterBottom>
      Open API Enabled: {renderBooleanFlag(props.openApiEnabled)}
    </Typography>
    <Typography variant="body2" gutterBottom>
      Chello Compliant: {renderBooleanFlag(props.chelloCompliant)}
    </Typography>
    <Typography variant="body2" gutterBottom>
      AI Suggestions: {getAiSuggestionsState(props.aiEnabled, props.aiBlocked) ?? defaultValue}
    </Typography>
    {props.lastBackfilled && (
      <Typography variant="body2" gutterBottom>
        Last Backfilled: {props.lastBackfilled}
      </Typography>
    )}
    {props.scheduledArchiveDate && (
      <Typography variant="body2" gutterBottom>
        Scheduled Deactivation: {props.scheduledArchiveDate}
      </Typography>
    )}
    {props.archivedAt && (
      <Typography variant="body2" gutterBottom>
        Archived At: {props.archivedAt}
      </Typography>
    )}
    {props.reasonToArchive && (
      <Typography variant="body2" gutterBottom>
        Reason to Archive: {props.reasonToArchive}
      </Typography>
    )}
  </>
);

type DetailedTeamCountsPropsType = {
  selectedTeam?: GenericTeamInterface;
  shouldShowDetailedLoading: boolean;
  csmName: string;
  internalContractorsCount?: number;
  isLoadingInternalContractors?: boolean;
};

/** Fields backed by the slow rails API, which render a Loading placeholder while pending. */
const DetailedTeamCounts = ({
  selectedTeam,
  shouldShowDetailedLoading,
  csmName,
  internalContractorsCount,
  isLoadingInternalContractors,
}: DetailedTeamCountsPropsType) => {
  const {
    actives_count,
    archived_members_count,
    created_at,
    financial_managers_count,
    workload_planners_count,
    guests_count,
    contractors_count,
    boards_count,
    admins_count,
    projects_count,
    project_tasks_count,
    time_entries_count,
    recurly_id,
  } = selectedTeam || {};

  const renderField = (
    value: number | string | undefined | React.ReactNode,
    label: string,
    isSpecialContent?: boolean,
    // Some fields load from a different source than the rails detailed payload
    // (e.g. the internal-contractor count from the statistics endpoint), so they
    // pass their own loading flag to avoid flashing N/A before it resolves.
    additionalLoading?: boolean,
  ) => {
    const showLoading = shouldShowDetailedLoading || additionalLoading;
    return (
      <Typography
        variant="body2"
        gutterBottom
        sx={isSpecialContent ? { display: 'flex', alignItems: 'center', gap: 0.5 } : undefined}
      >
        {label}:{' '}
        {showLoading ? (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            Loading...
          </Box>
        ) : isSpecialContent ? (
          value
        ) : (
          (value ?? defaultValue)
        )}
      </Typography>
    );
  };

  return (
    <>
      {renderField(recurly_id, 'Recurly ID')}

      {renderField(actives_count, 'Active users (excluding CSM accounts)')}
      {renderField(archived_members_count, 'Archived users')}

      <Typography variant="body2" gutterBottom>
        Signed up at:{' '}
        {shouldShowDetailedLoading ? (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            Loading...
          </Box>
        ) : (
          convertISODateToRegularDateString(created_at as string) || defaultValue
        )}
      </Typography>

      {renderField(admins_count, 'Admins count')}
      {renderField(financial_managers_count, 'Budget managers count')}
      {renderField(workload_planners_count, 'Work planners count')}
      {renderField(contractors_count, 'Active External Contractors count')}
      {renderField(
        internalContractorsCount,
        'Active Internal Contractors count',
        false,
        isLoadingInternalContractors,
      )}
      {renderField(guests_count, 'Active Guests count')}
      {renderField(boards_count, 'Boards count')}
      {renderField(projects_count, 'Projects count')}
      {renderField(project_tasks_count, 'Project tasks count')}
      {renderField(time_entries_count, 'Time entries count')}
      {renderField(
        csmName ? <EllipsisText text={csmName}>{csmName}</EllipsisText> : defaultValue,
        'CSM',
        true,
      )}
    </>
  );
};

function About(props: AboutSectionPropsType) {
  const { id } = props.selectedTeam || {};

  const { csmAssignmentByTeam } = useCsmAssignments();
  const csmAssignment = id ? csmAssignmentByTeam[id] : undefined;
  const csmName = csmAssignment?.account_name || props.searchApiCsm || '';

  const tenant = props.tenant || defaultValue;
  const realm = props.realm || defaultValue;
  const teamId = props.teamId || id;

  const isLoadingDetailed = props.isLoadingDetailedData ?? props.isLoadingTeamData ?? false;
  const isLoadingBasic = props.isLoadingBasicData ?? false;
  const hasBasicData = !!(props.tenant || props.realm || props.teamId);
  const hasDetailedData = !!props.selectedTeam;
  const shouldShowDetailedLoading = isLoadingDetailed && !hasDetailedData;

  const showError = !isLoadingBasic && !isLoadingDetailed && !hasBasicData && !hasDetailedData;

  return (
    <MinimalCard $hoverEffect={false}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          About
        </Typography>

        <Spacer mb={4} />

        {showError ? (
          <Typography variant="body2" color="error">
            Failed to fetch team information
          </Typography>
        ) : (
          <AboutSectionStyler>
            {/* Basic info - from fast search API, always try to show */}
            {conditionallyRenderElement({
              isLoading: isLoadingBasic && !hasBasicData,
              content: (
                <BasicTeamInfo
                  uuid={props.uuid}
                  tenant={tenant}
                  realm={realm}
                  realmType={props.realmType}
                  teamId={teamId}
                  nicknames={props.nicknames}
                  internalTestTeam={props.internalTestTeam}
                  ddiEnabled={props.ddiEnabled}
                  onEnableDdi={props.onEnableDdi}
                  isEnablingDdi={props.isEnablingDdi}
                  ssoEnabled={props.ssoEnabled}
                  openApiEnabled={props.openApiEnabled}
                  chelloCompliant={props.chelloCompliant}
                  aiEnabled={props.aiEnabled}
                  aiBlocked={props.aiBlocked}
                  lastBackfilled={props.lastBackfilled}
                  scheduledArchiveDate={props.scheduledArchiveDate}
                  archivedAt={props.archivedAt}
                  reasonToArchive={props.reasonToArchive}
                />
              ),
            })}

            {/* Detailed info - from slow rails API, show loading state if pending */}
            <DetailedTeamCounts
              selectedTeam={props.selectedTeam}
              shouldShowDetailedLoading={shouldShowDetailedLoading}
              csmName={csmName}
              internalContractorsCount={props.internalContractorsCount}
              isLoadingInternalContractors={props.isLoadingInternalContractors}
            />
          </AboutSectionStyler>
        )}
      </CardContent>
    </MinimalCard>
  );
}

export default About;
